import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, NgForm } from '@angular/forms';
import * as moment from 'moment';
import { DateRange, extendMoment } from 'moment-range';
import { Observable } from 'rxjs';

import { isLocalUsingMeridianNotation } from '../../../utils/meridian.helper';
import { Shift } from '../../shift-settings/models/shift';
import { ShiftErrorMessages } from '../../shift-settings/models/shift-error-messages';
import { AbstractModal } from '../AbstractModal';
import { ModalService } from '../modal.service';

@Component({
  selector: 'ls-shift-setting-modal',
  templateUrl: './shift-setting-modal.component.html',
  styleUrls: ['./shift-setting-modal.component.scss']
})
export class ShiftSettingModalComponent extends AbstractModal
  implements OnInit {
  @Input()
  public data: { shifts: Observable<Array<Shift>>; shift?: Shift };

  public loading = true;
  public error: string;
  public shift: Shift;
  public shiftStartGroup: FormGroup;
  public shiftEndGroup: FormGroup;

  private moment = extendMoment(moment);
  private shifts: Array<Shift>;

  constructor(private modal: ModalService, private formBuilder: FormBuilder) {
    super();
    this.overflowVisible = true;
    this.shift = new Shift('', { hour: 0, minute: 0 }, { hour: 0, minute: 0 });
  }

  public ngOnInit(): void {
    this.data.shifts.subscribe((shifts: any) => {
      this.shifts = shifts;
    });

    if (this.data.shift.id) {
      this.shift = { ...this.data.shift };
    } else {
      this.shift = new Shift(
        '',
        { hour: 0, minute: 0 },
        { hour: 0, minute: 0 }
      );
    }

    this.shiftStartGroup = this.formBuilder.group({
      hour: [
        isLocalUsingMeridianNotation() && this.shift.startTime.hour > 12
          ? this.formatTime(this.shift.startTime.hour - 12)
          : this.formatTime(this.shift.startTime.hour)
      ],
      minute: [this.formatTime(this.shift.startTime.minute)]
    });

    this.shiftEndGroup = this.formBuilder.group({
      hour: [
        this.shift.endTime.hour && this.shift.endTime.hour > 12
          ? this.formatTime(this.shift.endTime.hour - 12)
          : this.formatTime(this.shift.endTime.hour)
      ],
      minute: [this.formatTime(this.shift.endTime.minute)]
    });

    if (isLocalUsingMeridianNotation()) {
      this.shiftStartGroup.addControl(
        'meridian',
        new FormControl(this.shift.startTime.hour >= 12 ? 'pm' : 'am')
      );
      this.shiftEndGroup.addControl(
        'meridian',
        new FormControl(this.shift.endTime.hour >= 12 ? 'pm' : 'am')
      );
    }
  }

  public close(): void {
    this.modal.close();
  }

  public formatTime(minute: number): string {
    return moment({ minute }).format('mm');
  }

  public save(shiftForm: NgForm): void {
    if (
      shiftForm.valid &&
      this.shiftStartGroup.valid &&
      this.shiftEndGroup.valid
    ) {
      const shiftFormValue: Shift = shiftForm.value;
      if (this.shift && this.shift.id) {
        this.shift.name = shiftFormValue.name;
        const startHour: number = parseInt(
          this.shiftStartGroup.controls.hour.value,
          10
        );
        const endHour: number = parseInt(
          this.shiftEndGroup.controls.hour.value,
          10
        );

        this.shift.startTime = {
          hour:
            isLocalUsingMeridianNotation() &&
            this.shiftStartGroup.controls.meridian.value === 'pm' &&
            startHour !== 12
              ? startHour + 12
              : startHour,
          minute: parseInt(this.shiftStartGroup.controls.minute.value, 10)
        };
        this.shift.endTime = {
          hour:
            isLocalUsingMeridianNotation() &&
            this.shiftEndGroup.controls.meridian.value === 'pm' &&
            endHour !== 12
              ? endHour + 12
              : endHour,
          minute: parseInt(this.shiftEndGroup.controls.minute.value, 10)
        };
      } else {
        this.shift = new Shift(
          shiftFormValue.name,
          {
            hour: parseInt(this.shiftStartGroup.controls.hour.value, 10),
            minute: parseInt(this.shiftStartGroup.controls.minute.value, 10)
          },
          {
            hour: parseInt(this.shiftEndGroup.controls.hour.value, 10),
            minute: parseInt(this.shiftEndGroup.controls.minute.value, 10)
          }
        );
      }

      this.error = ShiftErrorMessages.OverLappingShift;
      if (this.areShiftsOverlapping(this.shift)) {
        this.error = ShiftErrorMessages.OverLappingShift;
      } else if (this.shift.name === '') {
        this.error = ShiftErrorMessages.NoName;
      } else {
        this.modal.close({ shift: this.shift, shifts: this.shifts });
        this.error = ShiftErrorMessages.NoError;
      }
    }
  }

  private areShiftsOverlapping(shiftToCheck: Shift): boolean {
    const range1: DateRange = this.moment.range(
      moment({
        hour: shiftToCheck.startTime.hour,
        minute: shiftToCheck.startTime.minute
      }),
      moment({
        hour: shiftToCheck.endTime.hour,
        minute: shiftToCheck.endTime.minute
      })
    );
    if (range1.end.isBefore(range1.start)) {
      range1.start.subtract(1, 'days');
    }
    return this.shifts.some((shift: Shift) => {
      if (shift.id !== shiftToCheck.id) {
        const range2: DateRange = this.moment.range(
          moment({
            hour: shift.startTime.hour,
            minute: shift.startTime.minute
          }),
          moment({ hour: shift.endTime.hour, minute: shift.endTime.minute })
        );
        if (range2.end.isBefore(range2.start)) {
          range2.start.subtract(1, 'days');
        }
        return range1.overlaps(range2);
      }
    });
  }
}
